import { BitmapFont, BitmapText, Container, Sprite, UPDATE_PRIORITY } from 'pixi.js'
import { hexCenter, dividerY } from '../../utils/hexGeometry'
import { LOAD } from '../load.config'

// HUD overlay drawn in PixiJS (item F raised load, 2026-10-07): LOAD.hudIcons
// icon/button sprites and LOAD.hudTexts live text objects, every text
// changing once per LOAD.hudTextUpdateMs (beat included).
//   14 corridor capacity labels (7 per side, from the fixture's boards)
//   20 icon captions
//    2 timers (round timer, beat clock)
//    4 player labels (names, yields)
// Screen space, above the scene, below the strobe/flash layers.
//
// Text is BitmapText (LOAD.hudTextMethod): glyphs from one atlas made at load
// (LOAD.hudGlyphs), so changing a text only re-lays out quads.
const GLYPHS = [['0', '9'], ['A', 'Z'], ['a', 'z'], ' :/·+-.']
const installed = new Set()
export function createHud({ app, layout, textures, fixture }) {
    const { R, offsetX, offsetY } = layout
    const W = app.screen.width, H = app.screen.height
    const root = new Container()
    root.label = 'hud'
    const fontSize = Math.round(Math.max(8, R * 0.62))
    const fontName = `gate1-hud-${fontSize}-${app.renderer.resolution}`
    if (!installed.has(fontName)) {
        BitmapFont.install({
            name: fontName,
            chars: GLYPHS,
            resolution: app.renderer.resolution,
            padding: 4,
            // No outline: outlined glyphs overlapped their neighbours in the
            // atlas (stray marks, clipped letters). A drop shadow keeps the text readable.
            style: { fontFamily: 'Menlo, monospace', fontSize, fill: 0xffffff, fontWeight: '700', dropShadow: { color: 0x000000, alpha: 0.9, blur: 0, distance: Math.max(1, fontSize / 8), angle: Math.PI / 4 } },
        })
        installed.add(fontName)
    }
    const style = { fontFamily: fontName, fontSize }
    const texts = []
    const addText = (x, y, initial, anchorX = 0.5) => {
        const t = new BitmapText({ text: initial, style })
        t.anchor.set(anchorX, 0.5)
        t.position.set(x, y)
        root.addChild(t)
        texts.push(t)
        return t
    }

    // Icons: two rows of LOAD.hudIcons / 2, top and bottom, with captions.
    const perRow = LOAD.hudIcons / 2
    const icons = []
    const iconTex = i => textures.get(`icon-${String(i).padStart(2, '0')}`)
    for (let i = 0; i < LOAD.hudIcons; i++) {
        const row = i < perRow ? 0 : 1
        const col = i % perRow
        const tex = iconTex(i)
        const sp = new Sprite(tex)
        sp.anchor.set(0.5)
        sp.width = tex.gate1Size.w
        sp.height = tex.gate1Size.h
        const x = (W / perRow) * (col + 0.5)
        const y = row === 0 ? Math.max(22, offsetY + R * 1.4) : Math.min(H - 30, offsetY + layout.height - R * 1.6)
        sp.position.set(x, y)
        sp.alpha = 0.92
        root.addChild(sp)
        icons.push(sp)
    }
    const CAPTIONS = ['BUY', 'MOV', 'BRD', 'DRN', 'RKT', 'SCN', 'YLD', 'INT', 'RNK', 'MNU']
    const captions = icons.map((sp, i) => addText(sp.position.x, sp.position.y + (i < perRow ? 1 : -1) * R * 1.35, `${CAPTIONS[i % CAPTIONS.length]} 0`))

    // Corridor capacities, both sides, at the band.
    const bandY = offsetY + dividerY(R)
    const capacities = []
    for (const [seat, side] of [['p1', 'self'], ['p2', 'opp']]) {
        const board = fixture.result[seat].board
        for (let c = 0; c < 7; c++) {
            const bf = board[c]
            const x = offsetX + hexCenter(side, c * 3 + 1, 0, R).x
            const y = bandY + (side === 'self' ? 1 : -1) * R * 1.1
            capacities.push({ text: addText(x, y, `${bf.armyQty ?? 0}/${bf.maxArmyQty ?? 0}`), armies: bf.armyQty ?? 0, cap: bf.maxArmyQty ?? 0 })
        }
    }

    // Timers and player labels.
    const roundTimer = addText(W / 2, bandY, '0:30')
    const beatClock = addText(W - 4, bandY, 't+0', 1)
    const labels = [
        addText(4, offsetY + layout.height * 0.25, `P2 ${fixture.meta.bots?.p2 ?? ''}`, 0),
        addText(4, offsetY + layout.height * 0.75, `P1 ${fixture.meta.bots?.p1 ?? ''}`, 0),
        addText(4, bandY - R * 2.6, `YLD ${fixture.result.p2.yield ?? 0}`, 0),
        addText(4, bandY + R * 2.6, `YLD ${fixture.result.p1.yield ?? 0}`, 0),
    ]
    if (texts.length !== LOAD.hudTexts) throw new Error(`HUD has ${texts.length} texts, load spec says ${LOAD.hudTexts}`)

    // Every text changes once a second.
    let tick = 0
    let last = performance.now()
    const update = () => {
        const now = performance.now()
        if (now - last < LOAD.hudTextUpdateMs) return
        last = now
        tick++
        const secs = Math.max(0, 30 - (tick % 31))
        roundTimer.text = `0:${String(secs).padStart(2, '0')}`
        beatClock.text = `t+${tick}`
        captions.forEach((t, i) => { t.text = `${CAPTIONS[i % CAPTIONS.length]} ${(tick * (i + 1)) % 100}` })
        capacities.forEach((c, i) => { c.text.text = `${c.armies}/${c.cap}${(tick + i) % 2 ? '·' : ' '}` })
        labels[2].text = `YLD ${(fixture.result.p2.yield ?? 0) + tick}`
        labels[3].text = `YLD ${(fixture.result.p1.yield ?? 0) + tick}`
        labels[0].text = `P2 ${fixture.meta.bots?.p2 ?? ''} ${tick % 10}`
        labels[1].text = `P1 ${fixture.meta.bots?.p1 ?? ''} ${tick % 10}`
    }
    app.ticker.add(update, null, UPDATE_PRIORITY.LOW)

    return {
        root,
        stats: () => ({ icons: icons.length, texts: texts.length, textUpdates: tick, textMethod: LOAD.hudTextMethod, glyphs: LOAD.hudGlyphs }),
        destroy() {
            app.ticker.remove(update)
            root.destroy({ children: true })
        },
    }
}
